import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Ship, ShieldCheck, Snowflake, FileCheck2, Globe2, Package, ArrowRight,
  Leaf, Coffee, Wheat, Drumstick, Sprout, Tag, Check, Sparkles, TrendingUp,
  Microscope, FileText, Truck, HandHeart, MapPin,
} from "lucide-react";
import { PRODUCT_DETAILS, PRODUCT_ORDER } from "@/lib/productData";
import ProductDetailModal from "@/components/ProductDetailModal";
import TradeCorridorMap from "@/components/TradeCorridorMap";

const WHY = [
  {
    icon: <ShieldCheck className="w-6 h-6" />,
    title: "Eliminate Execution Risk",
    body: "Single counterparty accountability across the entire supply chain. No fragmented vendors, no finger-pointing.",
    stat: "100%", statLabel: "Single-Point Accountability",
  },
  {
    icon: <FileCheck2 className="w-6 h-6" />,
    title: "Ensure Quality Consistency",
    body: "Rigorous QC protocols at every stage. Pre-shipment verification against your exact specifications, every time.",
    stat: "99.2%", statLabel: "QC Pass Rate",
  },
  {
    icon: <Snowflake className="w-6 h-6" />,
    title: "Remove Delivery Surprises",
    body: "Real-time visibility with proactive issue resolution. Know exactly where your cargo is and when it arrives.",
    stat: "90%+", statLabel: "On-Time Delivery",
  },
];

const STEPS = [
  { n: "01", t: "Origin Sourcing", d: "Direct farm partnerships across 15+ countries. Verified farms, fair pricing, aligned with your demand cycles.", stats: [["15+", "Active Origins"], ["200+", "Farm Partners"]], graphic: <Sprout className="w-7 h-7" /> },
  { n: "02", t: "Quality & Specs", d: "Pre-shipment verification. Third-party inspectors + our QC protocols verify grade, size, and compliance before cargo moves.", stats: [["99.2%", "QC Pass Rate"], ["500+", "Inspections/Month"]], graphic: <Microscope className="w-7 h-7" /> },
  { n: "03", t: "Cold-Chain & Logistics", d: "Temperature-controlled sea, air, and road. IoT-monitored cold-chain integrity over transit distance.", stats: [["99.8%", "Cold Chain Integrity"], ["40+", "Transit Countries"]], graphic: <Snowflake className="w-7 h-7" /> },
  { n: "04", t: "Compliance & Docs", d: "Phyto, CoO, PSQIR and all export documentation handled. Smooth customs clearance every time.", stats: [["100%", "Doc Accuracy"], ["99.5%", "Clearance Rate"]], graphic: <FileText className="w-7 h-7" /> },
  { n: "05", t: "Delivery & Resolution", d: "Precision final-mile delivery. 24/7 resolution team addresses issues, claims, and adjustments transparently.", stats: [["90%+", "On-Time Delivery"], ["<24h", "Resolution Time"]], graphic: <Truck className="w-7 h-7" /> },
  { n: "06", t: "Ongoing Supply", d: "Beyond single transactions. Programmatic supply, demand forecasting, and seasonal planning year-round.", stats: [["92%", "Repeat Clients"], ["3+ yrs", "Avg Partnership"]], graphic: <HandHeart className="w-7 h-7" /> },
];

const PRODUCT_ICONS = {
  "fresh-produce": <Leaf className="w-5 h-5" />,
  "pulses-grains": <Wheat className="w-5 h-5" />,
  "meat": <Drumstick className="w-5 h-5" />,
  "coffee": <Coffee className="w-5 h-5" />,
  "tea": <Sprout className="w-5 h-5" />,
  "oilseeds-nuts": <Package className="w-5 h-5" />,
  "private-label": <Tag className="w-5 h-5" />,
};

const TICKER = [
  "15+ countries",
  "90%+ on-time delivery",
  "24/7 trade operations",
  "Direct origin sourcing",
  "Program based supply",
];

// ---- Animated Counter ----
function useCountUp(target, duration = 1400, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startT = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - startT) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
}

function Counter({ to, suffix = "", decimals = 0, trigger }) {
  const v = useCountUp(Math.round(to * Math.pow(10, decimals)), 1500, trigger);
  const display = decimals ? (v / Math.pow(10, decimals)).toFixed(decimals) : v;
  return <>{display}{suffix}</>;
}

// ---- IntersectionObserver reveal ----
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }); },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, shown];
}

export default function Landing() {
  const [activeProduct, setActiveProduct] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  // Scroll to the hash target when arriving from another route (e.g. /#process).
  // We retry briefly so the target has time to mount before measuring offsets.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (tries++ < 20) setTimeout(tick, 80);
    };
    tick();
  }, [location.hash]);
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Nav />
      <Hero />
      <Ticker />
      <Why />
      <Process />
      <Products onOpen={(slug) => setActiveProduct(PRODUCT_DETAILS[slug])} />
      <CTA />
      <Footer />

      <ProductDetailModal
        product={activeProduct}
        onClose={() => setActiveProduct(null)}
        onContact={(slug) => nav(`/contact?product=${slug}`)}
      />
    </div>
  );
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 10);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  // When already on landing, smooth-scroll. Otherwise navigate home with hash;
  // Landing mounts and the hash-scroll effect there will jump to the section.
  const goSection = (id) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };
  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-stone-200 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
          <img src="/traderepubliq-logo.svg" alt="TradeRepubliq" className="h-7 md:h-8" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-stone-600">
          <a href="/#why" onClick={goSection("why")} className="hover:text-[#6B0F1A]" data-testid="nav-why">Why TradeRepubliq</a>
          <a href="/#process" onClick={goSection("process")} className="hover:text-[#6B0F1A]" data-testid="nav-process">Process</a>
          <a href="/#products" onClick={goSection("products")} className="hover:text-[#6B0F1A]" data-testid="nav-products">Products</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/contact" className="hidden sm:inline-flex bg-[#F4E8E9] text-[#6B0F1A] text-sm font-semibold rounded-xl px-4 py-2 hover:bg-[#EDD8DB]" data-testid="nav-talk">Talk to Us</Link>
          <Link to="/login" className="inline-flex bg-[#6B0F1A] text-white text-sm font-semibold rounded-xl px-4 py-2 hover:bg-[#56121C] shadow-lg shadow-[#6B0F1A]/25" data-testid="nav-signin">Sign In</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative isolate overflow-hidden min-h-[70vh] md:min-h-[80vh] flex items-center">
      {/* Background layers */}
      <div className="absolute inset-0 -z-10">
        <div className="mesh-blob mesh-blob-1" />
        <div className="mesh-blob mesh-blob-2" />
        <div className="mesh-blob mesh-blob-3" />
        <div className="absolute inset-0 dotgrid" />
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/30 to-white" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-24 text-center relative">
        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#6B0F1A]/15 text-[#6B0F1A] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6 shadow-sm hero-rise" style={{ animationDelay: "0.05s" }}>
          <Sparkles className="w-3.5 h-3.5" /> Global Agri-Commodities Trade Partner
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-[#1F1F1F] leading-[1.02] hero-rise" style={{ animationDelay: "0.15s" }}>
          Moving Agri Products.<br />
          From Origin to Market.
        </h1>
        <p className="text-stone-600 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed hero-rise" style={{ animationDelay: "0.3s" }}>
          We operate sourcing, quality, logistics, and delivery on trusted supply-chain rails so procurement teams can plan with confidence.
        </p>
        <div className="flex flex-wrap gap-3 mt-8 justify-center hero-rise" style={{ animationDelay: "0.45s" }}>
          <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#6B0F1A] text-white font-semibold rounded-xl px-5 py-3 hover:bg-[#56121C] shadow-xl shadow-[#6B0F1A]/30 shine" data-testid="hero-talk">
            Talk to Us <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <a href="#products" className="inline-flex items-center gap-2 bg-white text-[#6B0F1A] border-2 border-[#6B0F1A]/30 font-semibold rounded-xl px-5 py-3 hover:border-[#6B0F1A] hover:bg-[#FBFAF9] shadow-md shadow-[#6B0F1A]/5 transition-colors" data-testid="hero-products">
            View Product Portfolio
          </a>
        </div>
      </div>

      {/* Aurora band at bottom removed for a flat, even hero base */}
    </section>
  );
}

function HeroStat({ v, suffix, decimals, label, trigger }) {
  return (
    <div>
      <div className="font-display text-4xl font-extrabold text-[#6B0F1A] tabular-nums">
        <Counter to={v} suffix={suffix} decimals={decimals || 0} trigger={trigger} />
      </div>
      <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

// --- Ticker ---
function Ticker() {
  const items = [...TICKER, ...TICKER];
  return (
    <div className="relative border-y border-stone-200 bg-white overflow-hidden py-4">
      <div className="marquee-track">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 text-[#6B0F1A] whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A8323F] ticker-dot" />
            <span className="font-display font-semibold text-lg tracking-tight">{it}</span>
            <span className="text-stone-300 text-2xl">·</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Why ---
function Why() {
  return (
    <Section id="why" label="" title="Why TradeRepubliq" bg="white">
      <p className="text-stone-600 text-base mt-4 max-w-2xl">
        Built for procurement teams who need counterparty-grade reliability, not marketplace chaos.
      </p>
      <div className="grid md:grid-cols-3 gap-5 mt-12">
        {WHY.map((w, i) => (
          <RevealDiv key={w.title} delay={i * 120}>
            <div className="bg-white border border-stone-200 rounded-2xl p-6 glow-card h-full relative overflow-hidden">
              {/* decorative SVG leaf pattern (smaller & softer on mobile) */}
              <svg className="absolute -right-8 -bottom-8 w-24 sm:w-32 md:w-40 h-24 sm:h-32 md:h-40 text-[#F4E8E9] opacity-80 pointer-events-none" viewBox="0 0 100 100" fill="currentColor" aria-hidden>
                <path d="M50 10 Q20 30 30 70 Q40 85 55 70 Q70 50 50 10 Z" />
                <path d="M50 10 L50 70" stroke="#A8323F" strokeWidth="1" fill="none" opacity="0.18" />
              </svg>
              {/* gradient blob */}
              <div className="absolute -left-10 -top-10 w-32 h-32 rounded-full bg-gradient-to-br from-[#F4E8E9] to-transparent opacity-70 pointer-events-none" />

              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6B0F1A] to-[#A8323F] text-white flex items-center justify-center shadow-lg shadow-[#6B0F1A]/30">{w.icon}</div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-4">{w.title}</h3>
                <p className="text-stone-600 text-sm mt-2 leading-relaxed">{w.body}</p>
                <div className="mt-5 pt-4 border-t border-stone-100 flex items-baseline gap-2 flex-wrap">
                  <span className="font-display text-3xl font-extrabold grad-text">{w.stat}</span>
                  <span className="text-xs text-stone-500 font-semibold">{w.statLabel}</span>
                </div>
              </div>
            </div>
          </RevealDiv>
        ))}
      </div>
    </Section>
  );
}

// --- About ---
export function About() {
  return (
    <Section
      id="about"
      label="About TradeRepubliq"
      title="Not a Broker. A Trade Operator."
      bg="white"
    >
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 mt-8 items-start">
        <div className="space-y-5 text-stone-700 text-base leading-relaxed">
          <p>
            We are not a marketplace, broker, or exporter. We are a counterparty-grade trade
            operator that runs sourcing, quality, logistics, compliance, and delivery
            end-to-end for procurement teams who demand reliability.
          </p>
          <p>
            Our infrastructure spans multiple continents, connecting agricultural origins in
            East Africa, South Asia, and South America to markets worldwide through our Dubai hub.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-5 py-3 mt-2 shadow-lg shadow-[#6B0F1A]/20"
            data-testid="about-partner-cta"
          >
            Partner With Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { v: "15+", l: "Origin Countries" },
            { v: "40+", l: "Markets Served" },
            { v: "200+", l: "Farm Partners" },
          ].map((s) => (
            <div key={s.l} className="bg-[#FBFAF9] border border-stone-200 rounded-2xl p-5 text-center">
              <div className="font-display text-3xl font-extrabold text-[#6B0F1A]">{s.v}</div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mt-1">{s.l}</div>
            </div>
          ))}
          <div className="col-span-3 bg-white border border-stone-200 rounded-2xl overflow-hidden mt-2 shadow-sm">
            <div className="flex items-center gap-3 px-5 pt-5 pb-3">
              <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="font-display text-lg font-extrabold text-[#1F1F1F]">Dubai Headquarters</div>
            </div>
            <div className="relative" style={{ aspectRatio: "16/9" }}>
              <iframe
                title="TradeRepubliq Dubai office map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=55.2780%2C25.1390%2C55.3250%2C25.1665&layer=mapnik&marker=25.1525%2C55.3015"
                className="absolute inset-0 w-full h-full grayscale-[0.7] contrast-[0.95]"
                loading="lazy"
                style={{ border: 0 }}
              />
            </div>
            <div className="px-5 py-4 text-sm text-stone-700 space-y-1">
              <div>Meydan Grandstand, 6th Floor</div>
              <div className="text-stone-500">Meydan Road, Nad Al Sheba</div>
              <div className="text-stone-500">Dubai, United Arab Emirates</div>
              <div className="pt-1 text-stone-700">
                <span className="font-semibold">Phone:</span> +971 58 112 0336
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="text-xs uppercase tracking-widest font-bold text-[#A8323F] mb-2">Our Principles</div>
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1F1F1F] mb-6">How We Operate</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { t: "Execution Excellence", d: "We don't just source—we execute. Every shipment is managed end-to-end with full visibility and accountability." },
            { t: "Quality Integrity", d: "Every product meets rigorous quality standards. We own the QC process from origin to delivery." },
            { t: "Supply Certainty", d: "Procurement teams can plan with confidence. We deliver on time, every time, with transparent communication." },
          ].map((p) => (
            <div key={p.t} className="bg-white border border-stone-200 rounded-2xl p-5">
              <div className="font-semibold text-[#1F1F1F]">{p.t}</div>
              <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Trade Corridors */}
      <div className="mt-10">
        <div className="text-xs uppercase tracking-widest font-bold text-[#A8323F] mb-2">Global Network</div>
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1F1F1F]">Active Trade Corridors</h3>
        <p className="text-stone-600 mt-2 max-w-3xl leading-relaxed">
          Our supply chain infrastructure connects agricultural origins to markets worldwide through strategic hubs and established logistics networks.
        </p>

        <div className="mt-8">
          <TradeCorridorMap />
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {[
            { v: "8+", l: "Origin Countries Active" },
            { v: "40+", l: "Destination Markets" },
            { v: "12+", l: "Trade Lanes Operating" },
          ].map((s) => (
            <div key={s.l} className="bg-[#FBFAF9] border border-stone-200 rounded-xl px-4 py-3 text-center">
              <div className="font-display text-2xl font-extrabold text-[#6B0F1A]">{s.v}</div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// --- Process ---
function Process() {
  return (
    <Section id="process" label="Our Process" title="Trade Execution Model" bg="muted">
      <p className="text-stone-600 text-base mt-4 max-w-2xl">
        A complete end-to-end supply chain infrastructure, from origin to your warehouse.
      </p>

      <div className="relative mt-16">
        {/* Center timeline (desktop only) */}
        <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[3px] rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-stone-200" />
          <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-[#6B0F1A] via-[#A8323F] to-[#D7858C] timeline-draw" />
        </div>
        {/* Mobile timeline (left edge) */}
        <div className="md:hidden absolute left-5 top-0 bottom-0 w-[3px] rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-stone-200" />
          <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-[#6B0F1A] via-[#A8323F] to-[#D7858C] timeline-draw" />
        </div>

        <ol className="space-y-10 md:space-y-16">
          {STEPS.map((s, i) => (
            <ProcessStep key={s.n} step={s} index={i} />
          ))}
        </ol>

        {/* Final node */}
        <div className="relative flex md:justify-center mt-10">
          <div className="absolute md:static left-5 -translate-x-1/2 md:translate-x-0 w-4 h-4 rounded-full bg-[#6B0F1A] ring-4 ring-white shadow-lg shadow-[#6B0F1A]/30" />
          <div className="ml-12 md:ml-4 inline-flex items-center gap-2 bg-[#6B0F1A] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full shadow-lg shadow-[#6B0F1A]/30">
            <Check className="w-3.5 h-3.5" /> Delivered
          </div>
        </div>
      </div>
    </Section>
  );
}

function ProcessStep({ step, index }) {
  const [ref, shown] = useReveal(0.18);
  const isLeft = index % 2 === 0;
  return (
    <li ref={ref} className="relative">
      <div className="grid md:grid-cols-2 gap-x-20 items-center">
        {/* Spacer for alternating layout */}
        <div className={`hidden md:block ${isLeft ? "order-1" : "order-2"}`} />

        {/* Step card */}
        <div
          className={`relative pl-12 md:pl-0 ${isLeft ? "md:order-2" : "md:order-1 md:text-right"}`}
        >
          {/* Node on timeline — desktop: centered on timeline; mobile: left edge */}
          <div
            className={`absolute z-20 transition-all duration-500 top-6
              left-5 -translate-x-1/2
              ${isLeft ? "md:left-[-80px] md:-translate-x-0" : "md:left-auto md:right-[-80px] md:translate-x-0"}
              ${shown ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}
            aria-hidden
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#6B0F1A] blur-md opacity-50 animate-pulse" />
              <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#6B0F1A] to-[#56121C] text-white flex items-center justify-center font-display font-extrabold text-sm shadow-xl shadow-[#6B0F1A]/30 ring-4 ring-[#FBFAF9]">
                {step.n}
              </div>
            </div>
          </div>

          {/* Connector arrow desktop — bridges badge (on timeline) to card edge */}
          <div
            className={`hidden md:block absolute top-10 border-t-2 border-dashed transition-all duration-700 ${
              shown ? "border-[#A8323F] opacity-70" : "border-stone-300 opacity-0"
            } ${isLeft ? "left-[-58px] w-[58px]" : "right-[-58px] w-[58px]"}`}
          />

          {/* Card */}
          <div
            className={`bg-white border border-stone-200 rounded-2xl p-6 glow-card transition-all duration-700 relative overflow-hidden ${
              shown ? "translate-y-0 opacity-100" : isLeft ? "md:translate-x-6 translate-y-4 opacity-0" : "md:-translate-x-6 translate-y-4 opacity-0"
            }`}
            style={{ transitionDelay: `${shown ? 100 : 0}ms` }}
            data-testid={`process-step-${step.n}`}
          >
            <svg className="absolute -right-12 -top-12 w-40 h-40 text-[#F4E8E9]" viewBox="0 0 200 200" fill="none" aria-hidden>
              <circle cx="100" cy="100" r="90" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="100" cy="100" r="60" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <div className={`relative flex items-start gap-4 ${!isLeft ? "md:flex-row-reverse md:text-right" : ""}`}>
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6B0F1A] to-[#A8323F] text-white flex items-center justify-center shadow-lg shadow-[#6B0F1A]/25">
                {step.graphic}
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#A8323F]">Step {step.n}</div>
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-[#1F1F1F] mt-1">{step.t}</h3>
              </div>
            </div>
            <p className="text-stone-600 text-sm md:text-base mt-4 leading-relaxed relative">{step.d}</p>
            <div className={`mt-4 pt-4 border-t border-stone-100 flex gap-6 relative ${!isLeft ? "md:justify-end" : ""}`}>
              {step.stats.map(([v, l]) => (
                <div key={l}>
                  <div className="font-display text-xl font-extrabold text-[#6B0F1A]">{v}</div>
                  <div className="text-[11px] text-stone-500 font-semibold">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

// --- Products ---
function Products({ onOpen }) {
  return (
    <Section id="products" label="Our Products" title="Product Portfolio" bg="white">
      <p className="text-stone-600 text-base mt-4 max-w-2xl">
        Seven core categories, sourced from verified origins, delivered with institutional precision.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {PRODUCT_ORDER.map((slug, i) => {
          const p = PRODUCT_DETAILS[slug];
          return (
            <RevealDiv key={slug} delay={i * 70}>
              <button
                onClick={() => onOpen(slug)}
                className="w-full text-left bg-white border border-stone-200 rounded-2xl overflow-hidden glow-card h-full group relative"
                data-testid={`product-card-${slug}`}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3B0A11]/75 via-[#3B0A11]/15 to-transparent" />
                  <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur text-[#6B0F1A] flex items-center justify-center shadow-lg">
                    {PRODUCT_ICONS[slug]}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <h3 className="font-display text-xl font-extrabold tracking-tight text-white drop-shadow-lg">{p.title}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/85 bg-white/10 backdrop-blur border border-white/20 rounded-full px-2 py-0.5">Origin-verified</span>
                  </div>
                </div>
                {/* Body */}
                <div className="p-5 relative">
                  <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-[#F4E8E9] opacity-60 group-hover:scale-125 transition-transform duration-500 pointer-events-none" />
                  <p className="text-stone-600 text-sm leading-relaxed relative">{p.tagline.split(/\.\s/)[0] + "."}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4 relative">
                    {p.certs.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-stone-50 text-stone-600 border border-stone-200 rounded-full px-2 py-0.5">
                        <Check className="w-3 h-3 text-[#A8323F]" /> {c}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 text-sm font-bold text-[#6B0F1A] relative group-hover:translate-x-1 transition-transform">
                    View products <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </button>
            </RevealDiv>
          );
        })}
      </div>
    </Section>
  );
}

// --- CTA ---
function CTA() {
  return (
    <section id="contact" className="py-16 md:py-20 bg-[#3B0A11] relative overflow-hidden isolate">
      {/* Effects */}
      <div className="absolute inset-0 -z-10">
        <div className="mesh-blob" style={{ background: "#A8323F", top: "-20%", left: "10%", width: "40rem", height: "40rem", opacity: 0.3 }} />
        <div className="mesh-blob" style={{ background: "#6B0F1A", bottom: "-30%", right: "-10%", width: "44rem", height: "44rem", opacity: 0.4 }} />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute inset-0 grain opacity-30" />
      </div>

      {/* Floating leaf motifs */}
      <LeafMotif className="absolute top-10 left-8 w-20 h-20 text-[#A8323F] opacity-25 float-y" rotate="-20" />
      <LeafMotif className="absolute top-24 right-10 w-16 h-16 text-[#D7858C] opacity-20 float-y-slow" rotate="30" />
      <LeafMotif className="absolute bottom-20 left-14 w-24 h-24 text-[#A8323F] opacity-20 float-y-slow" rotate="60" />
      <LeafMotif className="absolute bottom-10 right-20 w-14 h-14 text-[#D7858C] opacity-30 float-y" rotate="-45" />

      <div className="max-w-4xl mx-auto px-6 text-center relative">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
          <Globe2 className="w-3.5 h-3.5" /> Ready to ship with confidence?
        </div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
          Plan your next shipment with <span className="grad-text" style={{ backgroundImage: "linear-gradient(120deg, #D7858C, #ffffff, #D7858C)" }}>counterparty-grade</span> reliability.
        </h2>
        <p className="text-white/70 mt-5 text-lg max-w-2xl mx-auto">
          Access your Operations Suite to manage your shipment end to end.
        </p>
        <div className="flex flex-wrap gap-3 justify-center mt-10">
          <Link to="/login" className="inline-flex items-center gap-2 bg-white text-[#3B0A11] font-semibold rounded-xl px-6 py-3.5 hover:bg-[#F4E8E9] shine shadow-2xl shadow-black/30" data-testid="cta-signin">
            <Ship className="w-4 h-4" /> Open Operations Suite
          </Link>
          <a href="mailto:support@traderepubliq.com" className="inline-flex items-center gap-2 bg-transparent text-white border border-white/30 font-semibold rounded-xl px-6 py-3.5 hover:bg-white/10" data-testid="cta-email">
            Email the team
          </a>
        </div>
      </div>
    </section>
  );
}

function LeafMotif({ className, rotate = 0 }) {
  return (
    <svg className={className} viewBox="0 0 100 100" fill="currentColor" style={{ transform: `rotate(${rotate}deg)` }}>
      <path d="M50 8 Q18 28 26 66 Q35 88 56 72 Q78 52 50 8 Z" />
      <path d="M50 8 L48 70" stroke="#ffffff" strokeWidth="1.5" fill="none" opacity="0.35" />
    </svg>
  );
}

// --- Footer ---
export function Footer() {
  return (
    <footer className="bg-[#1A0507] text-white/70 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-20" />
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-sm relative">
        <div>
          <div className="inline-flex">
            <img src="/traderepubliq-logo.svg" alt="TradeRepubliq" className="h-7 brightness-0 invert" />
          </div>
          <p className="mt-3 leading-relaxed">Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.</p>
        </div>
        <div>
          <div className="font-semibold text-white mb-3">Contact</div>
          <p>support@traderepubliq.com</p>
          <p className="mt-1">+971 58 112 0336</p>
        </div>
        <div>
          <div className="font-semibold text-white mb-3">Platform</div>
          <ul className="space-y-1">
            <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
            <li><Link to="/#why" className="hover:text-white">Why us</Link></li>
            <li><Link to="/#products" className="hover:text-white">Products</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-white mb-3">Company</div>
          <ul className="space-y-1">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 mt-8 pt-6 border-t border-white/10 text-xs text-white/50 relative">
        © {new Date().getFullYear()} TradeRepubliq LLC-FZ. All rights reserved.
      </div>
    </footer>
  );
}

// --- Helpers ---
function Section({ id, label, title, children, bg }) {
  const [ref, shown] = useReveal(0.1);
  return (
    <section id={id} className="py-12 md:py-20 relative isolate overflow-hidden bg-white">
      <div ref={ref} className={`max-w-6xl mx-auto px-6 reveal ${shown ? "in" : ""}`}>
        {label ? <div className="text-xs uppercase tracking-widest font-bold text-[#A8323F]">{label}</div> : null}
        <h2 className={`font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-[#1F1F1F] max-w-3xl leading-[1.05] ${label ? "mt-2" : ""}`}>
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

function RevealDiv({ children, delay = 0 }) {
  const [ref, shown] = useReveal(0.12);
  return (
    <div ref={ref} className={`reveal ${shown ? "in" : ""}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}
